/** Purchaser-only financial facts from the settled order, not today's event
 * pricing. Never attach this to an attendee's individual or claimed ticket. */
export type TicketReceipt = {
  orderId: number;
  eventSlug: string;
  quantity: number;
  unitAmountCents: number;
  subtotalCents: number;
  discountCents: number;
  additionalChargesCents: number;
  totalCents: number;
  currency: string;
  pricingLabel: string;
  promotionCode: string | null;
  paidAt: string | null;
};

export function formatReceiptMoney(cents: number, currency: string): string {
  if (!Number.isSafeInteger(cents) || !/^[a-z]{3}$/i.test(currency)) throw new Error("Invalid receipt amount.");
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

export function buildTicketReceipt(order: {
  id: number; eventSlug: string; status: string; quantity: number;
  unitAmountCents: number; amountTotalCents: number; currency: string;
  priceSource: string; promoCodeApplied: string | null; paidAt: Date | string | null;
}): TicketReceipt | null {
  if (order.status !== "paid" || !order.paidAt) return null;
  const paidAt = new Date(order.paidAt);
  const subtotalCents = order.unitAmountCents * order.quantity;
  if (Number.isNaN(paidAt.getTime()) || !/^[a-z]{3}$/i.test(order.currency)
    || ![order.id, order.quantity, order.unitAmountCents, order.amountTotalCents, subtotalCents].every(v => Number.isSafeInteger(v) && v >= 0)
    || order.id < 1 || order.quantity < 1) return null;
  // Checkout currently does not enable tax or shipping. These are the settled
  // net reduction/addition, not invented coupon percentages or list-price savings.
  // If tax/shipping is introduced, persist its separate settlement breakdown.
  return {
    orderId: order.id, eventSlug: order.eventSlug, quantity: order.quantity,
    unitAmountCents: order.unitAmountCents, subtotalCents,
    discountCents: Math.max(0, subtotalCents - order.amountTotalCents),
    additionalChargesCents: Math.max(0, order.amountTotalCents - subtotalCents),
    totalCents: order.amountTotalCents, currency: order.currency.toLowerCase(),
    pricingLabel: order.priceSource === "promo_lny2027" ? "Special event rate" : "Standard ticket price",
    promotionCode: order.promoCodeApplied, paidAt: paidAt.toISOString(),
  };
}
